import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Person, Email, Phone, Lock } from '@mui/icons-material';

const ROLES = [
  { value: 'member', label: 'Membro' },
  { value: 'teacher', label: 'Docente' },
  { value: 'admin', label: 'Amministratore' },
];

const SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

const LEVEL_OPTIONS = [
  { value: 0, label: 'Da assegnare' },
  { value: 1, label: '1° anno' },
  { value: 2, label: '2° anno' },
  { value: 3, label: '3° anno' },
  { value: 4, label: '4° anno' },
  { value: 5, label: '5° anno' },
  { value: 6, label: 'Oltre il 5° anno' },
];

function generateSixDigitPassword() {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

const ModalUserCreate = ({ open, onClose, onSubmit, loading, error }) => {
  const [password, setPassword] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    telephone: '',
    telephoneSecondary: '',
    shirtSize: '',
    level: 0,
    roles: ['member'],
    flagPicture: false,
  });

  useEffect(() => {
    if (open) {
      setPassword(generateSixDigitPassword());
      setFormData({
        name: '',
        surname: '',
        email: '',
        telephone: '',
        telephoneSecondary: '',
        shirtSize: '',
        level: 0,
        roles: ['member'],
        flagPicture: false,
      });
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'roles') {
        const roles = typeof value === 'string' ? value.split(',') : value;
        next.roles = roles;
        if (!roles.includes('member')) next.level = '';
      }
      return next;
    });
  };

  const handleLevelChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      level: value === '' || value == null ? 0 : Number(value),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const levelNum = formData.level === '' || formData.level == null ? 0 : Number(formData.level);
    onSubmit({
      ...formData,
      level: levelNum,
      password,
      flagStatuto: true,
      flagPrivacy: true,
      flagPicture: formData.flagPicture ?? false,
    });
  };

  const levelValue = formData.level === '' || formData.level == null ? 0 : formData.level;
  const isMember = formData.roles?.includes('member');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Crea utente</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                name="name"
                label="Nome"
                value={formData.name}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                name="surname"
                label="Cognome"
                value={formData.surname}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                required
                fullWidth
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                fullWidth
                name="telephone"
                label="Telefono"
                value={formData.telephone}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="telephoneSecondary"
                label="Telefono secondario"
                value={formData.telephoneSecondary}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Taglia maglietta</InputLabel>
                <Select
                  name="shirtSize"
                  value={formData.shirtSize}
                  label="Taglia maglietta"
                  onChange={handleChange}
                >
                  {SHIRT_SIZES.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required disabled={!isMember}>
                <InputLabel>Anno di corso</InputLabel>
                <Select
                  name="level"
                  value={levelValue}
                  label="Anno di corso"
                  onChange={handleLevelChange}
                >
                  {LEVEL_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Ruolo</InputLabel>
                <Select
                  name="roles"
                  multiple
                  value={formData.roles || []}
                  label="Ruolo"
                  onChange={handleChange}
                  renderValue={(selected) => selected.map((r) => ROLES.find((o) => o.value === r)?.label || r).join(', ')}
                >
                  {ROLES.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Password"
                value={password}
                inputProps={{ readOnly: true }}
                helperText="Codice numerico di 6 cifre inviato all'utente via email. Non modificabile."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="flagPicture"
                    checked={formData.flagPicture ?? false}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label="Accetto liberatoria foto"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>Annulla</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Crea utente'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ModalUserCreate;
